import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { createPortal } from 'react-dom';
import { getAll } from '../api';
import { useTableFilters } from '../utils/useTableFilters';
import { formatIntDate, parseUtc } from '../utils/dateUtils';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  listStocktakes,
  createStocktake,
  deleteStocktake,
  listStocktakeLines,
  updateStocktakeLineCount,
  finaliseStocktake,
  cancelStocktake,
} from '../api/stocktakesAPI';

const statusBadgeClass = (status = '') => {
  const normalized = status.toLowerCase();
  if (normalized.includes('approved')) return 'bg-success-focus text-success-main';
  if (normalized.includes('posted')) return 'bg-primary-focus text-primary-main';
  if (normalized.includes('counting_complete')) return 'bg-primary-focus text-primary-main';
  if (normalized.includes('draft')) return 'bg-secondary-focus text-muted';
  if (normalized.includes('cancelled')) return 'bg-danger-focus text-danger-main';
  if (normalized.includes('active')) return 'bg-info-focus text-info-main';
  return 'bg-secondary-focus text-secondary-main';
};

const StocktakesLayer = () => {
  const userProfile = useUserProfile();
  const userId = Number(userProfile?.id) || null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState('startedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [stocktakeName, setStocktakeName] = useState('');
  const [startErrors, setStartErrors] = useState({});
  const [activeStocktake, setActiveStocktake] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [lineSearch, setLineSearch] = useState('');
  const [lineRows, setLineRows] = useState([]);
  const [linePage, setLinePage] = useState(1);
  const [lineHasMore, setLineHasMore] = useState(true);
  const [lineLoading, setLineLoading] = useState(false);
  const [lineSaving, setLineSaving] = useState({});
  const [unlockedLineIds, setUnlockedLineIds] = useState({});
  const lineContainerRef = useRef(null);
  const isReadonly = ['finalised', 'finalized', 'cancelled', 'canceled'].includes(String(activeStocktake?.status || '').toLowerCase());

  const tableFilters = useMemo(() => [
    { field: 'warehouseId', type: 'dropDown', label: 'Warehouse', api: 'locations', displayField: 'name', menuBarFilter: 1 },
    { field: 'status', type: 'dropDown', label: 'Status', options: [{ value: 'draft', label: 'Draft' }, { value: 'in_progress', label: 'In Progress' }, { value: 'finalised', label: 'Finalised' }, { value: 'cancelled', label: 'Cancelled' }], menuBarFilter: 1 },
    { field: 'startedBy', type: 'text', label: 'Started by', menuBarFilter: 1 },
    { field: 'startedFrom', type: 'date', label: 'Started from', menuBarFilter: 0 },
    { field: 'startedTo', type: 'date', label: 'Started to', menuBarFilter: 0 },
  ], []);

  const { appliedFilters, renderFilterControls } = useTableFilters(tableFilters, {});

  const fetchStocktakes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listStocktakes({ ...appliedFilters, search: searchTerm, page, pageSize, sortField, sortOrder });
      const data = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      const usersById = new Map(users.map((user) => [Number(user.id), user.name]));
      const mapped = data.map((row) => ({
        ...row,
        startedByName: row.startedByName || usersById.get(Number(row.startedBy)) || row.startedBy,
      }));
      setRows(mapped);
      setTotal(response?.total ?? mapped.length);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, pageSize, searchTerm, sortField, sortOrder, users]);

  useEffect(() => { fetchStocktakes(); }, [fetchStocktakes]);
  useEffect(() => { getAll('locations').then((res) => setWarehouses(Array.isArray(res) ? res : res?.data || [])); }, []);
  useEffect(() => { getAll('users').then((res) => setUsers(Array.isArray(res) ? res : res?.data || [])); }, []);
  useEffect(() => { setHeaderSearchContainer(document.getElementById('header-search-container')); }, []);

  const loadLines = useCallback(async (stocktakeId, nextPage = 1, replace = false) => {
    if (!stocktakeId || lineLoading) return;
    setLineLoading(true);
    try {
      const response = await listStocktakeLines(stocktakeId, { page: nextPage, pageSize: 30, search: lineSearch });
      const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];

      setLineRows((prev) => {
        const merged = replace ? items : [...prev, ...items];
        const unique = [];
        const seen = new Set();
        merged.forEach((item) => { if (!seen.has(item.id)) { seen.add(item.id); unique.push(item); } });
        return unique;
      });
      const loaded = (replace ? items.length : lineRows.length + items.length);
      const totalCount = response?.total ?? loaded;
      setLineHasMore(loaded < totalCount && items.length > 0);
      setLinePage(nextPage);
    } finally { setLineLoading(false); }
  }, [lineLoading, lineRows.length, lineSearch]);

  const openDetail = async (row) => {
    const usersById = new Map(users.map((user) => [Number(user.id), user.name]));
    setUnlockedLineIds({});
    setActiveStocktake({
      ...row,
      startedByName: row.startedByName || usersById.get(Number(row.startedBy)) || row.startedBy,
    });
    setShowDetailModal(true);
    setLineSearch('');
    setLineRows([]);
    setLineHasMore(true);
    await loadLines(row.id, 1, true);
  };

  const closeDetailModal = async () => {
    setShowDetailModal(false);
    await fetchStocktakes();
  };

  const buildDefaultStocktakeName = useCallback((warehouseId) => {
    const id = Number(warehouseId);
    const selectedWarehouse = warehouses.find((warehouse) => Number(warehouse.id) === id);
    const createdAt = new Date();
    return `Stocktake ${selectedWarehouse?.name || `Warehouse ${id}`} ${createdAt.toLocaleDateString('en-CA')}`;
  }, [warehouses]);

  const handleStartStocktake = async () => {
    if (!selectedWarehouseId) return setStartErrors({ warehouseId: 'Warehouse is required.' });

    const warehouseId = Number(selectedWarehouseId);
    const createdAt = new Date();
    const createdAtStamp = `${createdAt.getUTCFullYear()}${String(createdAt.getUTCMonth() + 1).padStart(2, '0')}${String(createdAt.getUTCDate()).padStart(2, '0')}${String(createdAt.getUTCHours()).padStart(2, '0')}${String(createdAt.getUTCMinutes()).padStart(2, '0')}${String(createdAt.getUTCSeconds()).padStart(2, '0')}`;

    const created = await createStocktake({
      warehouseId,
      stockTakeNumber: `ST-${warehouseId}-${createdAtStamp}`,
      name: stocktakeName.trim() || buildDefaultStocktakeName(warehouseId),
      startedAt: new Date().toISOString(),
      startedBy: userId,
    });
    setShowStartModal(false);
    setSelectedWarehouseId('');
    setStocktakeName('');
    setStartErrors({});
    await fetchStocktakes();
    if (created?.id) openDetail(created);
  };

  const groupedLines = useMemo(() => lineRows.reduce((acc, row) => {
    const key = row.locationCode || 'Unassigned location';
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {}), [lineRows]);

  return <>
    {headerSearchContainer &&
      createPortal(
        <input
          type='text'
          className='form-control form-control-sm'
          style={{ width: '200px' }}
          placeholder='Search...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />,
        headerSearchContainer,
      )}
    <div className='card h-100 p-0 rounded-0'>
      <div className='card-header border-0 pb-16'>
        <div className='d-flex justify-content-between align-items-start gap-3'>
          <div className='flex-grow-1'>{renderFilterControls()}</div>
          <button className='btn btn-primary text-sm py-12 px-16 radius-4' onClick={() => { setShowStartModal(true); setSelectedWarehouseId(''); setStocktakeName(''); setStartErrors({}); }}>Start New Stocktake</button>
        </div>
        <div className='card-body p-0'>
          <div className='table-responsive scroll-sm'>
            <table className='table bordered-table mb-0 w-100 rounded-0'>
              <thead><tr><th>Reference</th><th>Warehouse</th><th>Status</th><th>Time started</th><th>Started by</th><th>Items</th><th>Counted</th><th>Progress</th><th>Time completed</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? <tr>
                  <td colSpan='10' className='text-center py-4'>Loading...</td>
                </tr> : rows.map((row) => {
                  const totalItems = Number(row.totalItems ?? row.itemCount ?? 0);
                  const countedItems = Number(row.countedItems ?? 0);
                  const progress = totalItems > 0 ? (countedItems / totalItems) * 100 : 0;
                  return <tr key={row.id}>
                    <td>{row.reference || row.number || `ST-${row.id}`}</td>
                    <td>{row.warehouseName || row.warehouse?.name || '-'}</td>
                    <td>
                      <span className={`badge text-sm ${statusBadgeClass(row.status)}`}>{row.status || 'Draft'}</span></td>
                    <td>{row.startedAt ? formatIntDate(parseUtc(row.startedAt)) : '-'}</td>
                    <td>{row.startedByName || row.startedBy || '-'}</td>
                    <td>{totalItems}</td>
                    <td>{countedItems}</td>
                    <td>
                      <div className='d-flex align-items-center gap-2'>
                        <div className='progress w-100' style={{ height: '6px', maxWidth: 120 }}>
                          <div className='progress-bar' style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} /></div>
                        <span>{progress.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td>{row.completedAt ? formatIntDate(parseUtc(row.completedAt)) : '-'}</td>
                    <td>
                      <div className='d-flex gap-2'>
                        <button
                          className='w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => openDetail(row)} title='View'><Icon icon='mdi:eye-outline' className='icon text-xl line-height-1' />
                        </button>
                        <button
                          disabled={String(row.status || '').toLowerCase().includes('final')}
                          className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={async () => {
                            if (!window.confirm('Are you sure you want to delete this stocktake?')) return;
                            await deleteStocktake(row.id).catch(() => cancelStocktake(row.id)); await fetchStocktakes();
                          }}
                          title='Delete'><Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                        </button>
                      </div>
                    </td>
                  </tr>;
                })}
                {!loading && rows.length === 0 && <tr><td colSpan='10' className='text-center py-4'>No stocktakes found.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className='d-flex justify-content-between p-3'><small>{total} stocktakes</small><div className='d-flex gap-2'><button className='btn btn-sm btn-light' disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button><button className='btn btn-sm btn-light' disabled={rows.length < pageSize} onClick={() => setPage((p) => p + 1)}>Next</button></div></div>
        </div>

        {showStartModal &&
          <div className='modal show d-block' tabIndex='-1'>
            <div className='modal-dialog'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Start Stocktake</h5>
                  <button className='btn-close' onClick={() => setShowStartModal(false)} /></div>
                <div className='modal-body'>
                  {/** Posted stocktakes are read-only in details view. */}
                  <label className='form-label'>Warehouse *</label>
                  <select
                    className={`form-select ${startErrors.warehouseId ? 'is-invalid' : ''}`}
                    value={selectedWarehouseId}
                    onChange={(e) => setSelectedWarehouseId(e.target.value)}>
                    <option value=''>Select warehouse</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>{startErrors.warehouseId &&
                    <div className='invalid-feedback'>{startErrors.warehouseId}</div>}
                </div>
                <div className='modal-footer'>
                  <button className='btn btn-secondary' onClick={() => setShowStartModal(false)}>Cancel</button>
                  <button className='btn btn-primary' onClick={handleStartStocktake}>Start</button>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
      {showStartModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog'>
            <div className='modal-content'>

              <div className='modal-header'>
                <h5 className='modal-title'>
                  Start Stocktake
                </h5>

                <button
                  className='btn-close'
                  onClick={() => setShowStartModal(false)}
                />
              </div>

              <div className='modal-body'>

                <label className='form-label'>
                  Warehouse *
                </label>

                <select
                  className={`form-select ${startErrors.warehouseId ? 'is-invalid' : ''
                    }`}
                  value={selectedWarehouseId}
                  onChange={(e) => {
                    const warehouseId = e.target.value;

                    setSelectedWarehouseId(warehouseId);
                    setStocktakeName(buildDefaultStocktakeName(warehouseId));
                  }}
                >
                  <option value=''>
                    Select warehouse
                  </option>

                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>

                {startErrors.warehouseId && (
                  <div className='invalid-feedback'>
                    {startErrors.warehouseId}
                  </div>
                )}

                <label className='form-label mt-3'>
                  Name
                </label>

                <input
                  className='form-control'
                  placeholder={
                    selectedWarehouseId
                      ? buildDefaultStocktakeName(selectedWarehouseId)
                      : 'Select a warehouse to generate a name'
                  }
                  value={stocktakeName}
                  onChange={(e) => setStocktakeName(e.target.value)}
                />
              </div>

              <div className='modal-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={() => setShowStartModal(false)}
                >
                  Cancel
                </button>

                <button
                  className='btn btn-primary'
                  onClick={handleStartStocktake}
                >
                  Start Stocktake
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDetailModal && activeStocktake && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog modal-xl modal-dialog-scrollable'>
            <div className='modal-content'>

              <div className='modal-header'>
                <h5 className='modal-title'>
                  Stocktake {activeStocktake.stockTakeNumber}
                </h5>

                <button
                  className='btn-close'
                  onClick={closeDetailModal}
                />
              </div>

              <div className='modal-body'>

                <div className='row g-3 mb-4'>
                  <div className='col-md-3'>
                    <small className='text-muted d-block'>Warehouse</small>
                    <strong>{activeStocktake?.warehouse?.name || '-'}</strong>
                  </div>

                  <div className='col-md-3'>
                    <small className='text-muted d-block'>Status</small>

                    <span
                      className={`badge bg-primary text-sm ${statusBadgeClass(activeStocktake.status)}`}
                    >
                      {activeStocktake.status}
                    </span>
                  </div>

                  <div className='col-md-3'>
                    <small className='text-muted d-block'>Started</small>

                    <strong>
                      {activeStocktake.startedAt
                        ? formatIntDate(parseUtc(activeStocktake.startedAt))
                        : '-'}
                    </strong>
                  </div>

                  <div className='col-md-3'>
                    <small className='text-muted d-block'>Started by</small>
                    <strong>{activeStocktake.startedByName || '-'}</strong>
                  </div>
                </div>

                <input
                  className='form-control mb-3'
                  placeholder='Search'
                  value={lineSearch}
                  onChange={(e) => setLineSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setLineRows([]);
                      setLineHasMore(true);
                      loadLines(activeStocktake.id, 1, true);
                    }
                  }}
                />

                <div
                  ref={lineContainerRef}
                  style={{
                    maxHeight: '55vh',
                    overflowY: 'auto',
                  }}
                  onScroll={(e) => {
                    const el = e.currentTarget;

                    if (
                      lineHasMore &&
                      !lineLoading &&
                      el.scrollTop + el.clientHeight >= el.scrollHeight - 120
                    ) {
                      loadLines(activeStocktake.id, linePage + 1);
                    }
                  }}
                >
                  {Object.entries(groupedLines).map(([locationCode, entries]) => (
                    <div key={locationCode} className='mb-4'>

                      <div className='fw-semibold border-bottom pb-2 mb-2'>
                        {locationCode}
                      </div>

                      <table className='table bordered-table mb-0'>
                        <thead>
                          <tr>
                            <th>Number</th>
                            <th>Description</th>
                            <th>Expected</th>
                            {activeStocktake.status !== 'posted' ? <th>Actual</th> : <th>Counted</th>}
                            <th>Variance</th>
                            <th>Status</th>
                            {activeStocktake.status === 'posted' && <th>Action</th>}
                          </tr>
                        </thead>

                        <tbody>
                          {entries.map((line) => {
                            const expected = Number(line.snapshotQty ?? 0);
                            const hasCountedQty = line.countedQty !== null && line.countedQty !== undefined && line.countedQty !== '';
                            const countedQty = Number(line.countedQty ?? 0);
                            const currentActual = Number(line.actualQuantity ?? 0);
                            const displayedActual = hasCountedQty ? countedQty : currentActual;
                            const variance = displayedActual - expected;
                            const isPosted = activeStocktake.status === 'posted';
                            const isUnlocked = Boolean(unlockedLineIds[line.id]);
                            const isInputLocked = isReadonly || (hasCountedQty && !isUnlocked);

                            const countStatus = hasCountedQty
                              ? 'Counted'
                              : displayedActual === 0
                                ? 'Uncounted'
                                : variance === 0
                                  ? 'Counted'
                                  : 'Variance';

                            return (
                              <tr key={line.id}>
                                <td>
                                  {line.stockItem?.materialNumber || '-'}
                                </td>

                                <td>
                                  {line.stockItem?.description || '-'}
                                </td>

                                <td>{expected}</td>

                                {isPosted ? (
                                  <td>{countedQty}</td>
                                ) : (
                                  <td>
                                    <input
                                      type='number'
                                      min='0'
                                      disabled={isInputLocked}
                                      className='form-control form-control-sm'
                                      value={line.actualQuantity ?? (hasCountedQty ? line.countedQty : '')}
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        setLineRows((prev) =>
                                          prev.map((p) =>
                                            p.id === line.id
                                              ? {
                                                ...p,
                                                actualQuantity: value,
                                              }
                                              : p
                                          )
                                        );
                                      }}
                                      onBlur={async () => {
                                        if (isInputLocked) return;

                                        const value = Number(
                                          lineRows.find((r) => r.id === line.id)
                                            ?.actualQuantity ?? 0
                                        );

                                        setLineSaving((prev) => ({
                                          ...prev,
                                          [line.id]: true,
                                        }));

                                        await updateStocktakeLineCount(
                                          activeStocktake.id,
                                          line.id,
                                          value
                                        );

                                        setLineRows((prev) => prev.map((p) => (p.id === line.id ? { ...p, actualQuantity: value, countedQty: value } : p)));
                                        setUnlockedLineIds((prev) => ({ ...prev, [line.id]: false }));
                                        setLineSaving((prev) => ({
                                          ...prev,
                                          [line.id]: false,
                                        }));
                                      }}
                                    />
                                  </td>
                                )}

                                <td>{variance}</td>
                                <td>
                                  <div className='d-flex align-items-center justify-content-between'>
                                    <span
                                      className={`badge ${countStatus === 'Variance'
                                          ? 'bg-warning-focus text-warning-main'
                                          : countStatus === 'Counted'
                                            ? 'bg-success-focus text-success-main'
                                            : 'bg-secondary-focus text-secondary-main'
                                        }`}
                                    >
                                      {lineSaving[line.id]
                                        ? 'Saving...'
                                        : countStatus}
                                    </span>

                                    {hasCountedQty && !isPosted && !isReadonly && !isUnlocked && (
                                      <button
                                        type='button'
                                        className='w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center'
                                        style={{ width: '32px', height: '32px' }}
                                        onClick={() => setUnlockedLineIds((prev) => ({ ...prev, [line.id]: true }))}
                                      >
                                        <Icon icon='mdi:lock-outline' className='icon text-xl line-height-1' />
                                      </button>
                                    )}
                                  </div>
                                </td>

                                {isPosted && (
                                  <td>
                                    <button
                                      type='button'
                                      className='btn btn-primary btn-sm py-2'
                                    >
                                      Adjust
                                    </button>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}

                  {lineLoading && (
                    <div className='text-center py-3'>
                      Loading more...
                    </div>
                  )}

                  {!lineLoading && lineRows.length === 0 && (
                    <div className='text-center py-3'>
                      No stocktake lines found.
                    </div>
                  )}
                </div>
              </div>

              <div className='modal-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={closeDetailModal}
                >
                  Close
                </button>

                <button
                  disabled={isReadonly || activeStocktake.status === 'posted' || lineRows.length === 0}
                  className='btn btn-danger'
                  onClick={async () => {
                    if (!window.confirm('Cancel this stocktake?')) return;

                    await cancelStocktake(activeStocktake.id, {
                      cancelledBy: userId,
                      cancelledAt: new Date().toISOString(),
                    });

                    await closeDetailModal();
                  }}
                >
                  Cancel
                </button>

                <button
                  disabled={isReadonly || activeStocktake.status === 'posted' || lineRows.length === 0}
                  className='btn btn-primary'
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Finalise stocktake? This will create inventory adjustments.'
                      )
                    ) {
                      return;
                    }

                    await finaliseStocktake(activeStocktake.id, {
                      completedBy: userId,
                      completedAt: new Date().toISOString(),
                    });

                    await closeDetailModal();
                  }}
                >
                  Finalise
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </>;
};

export default StocktakesLayer;
